import {
  Board,
  Column,
  FlushPanelContent,
  Panel,
  PanelHeader,
  PanelHeading,
} from '@gamma/ui/layout';
import moment from 'moment';
import { useContext, useMemo, useState } from 'react';
import { safeParse } from 'secure-json-parse';
import { useManualPagination } from '../../../../shared/hooks';
import { i18n } from '../../../../shared/localization';
import { OrgTenantsContext } from '../../../../shared/providers';
import { useGetUserLogs } from '../../../user/queries';

import { Box, Tooltip } from '@chakra-ui/react';
import InfoSharpIcon from '@gamma/ui/icons/InfoSharpIcon';
import { SecurityAuditTable } from './SecurityAuditTable';
import { SecurityAuditToolbar } from './SecurityAuditToolbar';
import { DateRange, Filters } from './types';

export interface Logs {
  event_category: string;
  message: string;
  timestamp: number;
  user_id: string;
  audit_type: string;
  tenant: string;
  display_name: string;
}

export const SecurityAudit = () => {
  const { orgTenantsQueryParam } = useContext(OrgTenantsContext);
  const date = new Date();

  const startDate = moment(date.setDate(date.getDate() - 7))
    .utc()
    .unix()
    .toString();
  const endDate = moment(Date.now()).utc().unix().toString();

  const { title, logsLimitMessage } = i18n.pages.securityAudit;
  const [filters, setFilters] = useState<Filters>({});
  const [dateRange, setDateRange] = useState<DateRange>({ startDate, endDate });

  const {
    offset,
    sortBy,
    pageSize,
    pageIndex,
    pageCount,
    onFetchData,
    getPageCount,
    getSortsForAPI,
    resetPagination,
  } = useManualPagination({
    sortBy: [{ id: 'timestamp', desc: true }],
  });

  const sorts = useMemo(
    () => [...getSortsForAPI(sortBy)],
    [getSortsForAPI, sortBy],
  );

  const variables = useMemo(() => {
    return {
      input: {
        filters,
        ...dateRange,
        offset,
        size: pageSize,
        sort: sorts,
        tenants: orgTenantsQueryParam,
      },
    };
  }, [filters, dateRange, offset, pageSize, sorts, orgTenantsQueryParam]);

  const { data, loading, error } = useGetUserLogs({
    variables,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      getPageCount(res?.getUserLogs);
    },
  });

  const logs: Logs[] = useMemo(
    () =>
      data?.getUserLogs?.log?.map((item) => {
        const log = safeParse(item?.message);
        return {
          ...log,
          display_name: item?.tenant_info?.tenant_display_name,
        };
      }) ?? [],
    [data?.getUserLogs?.log],
  );

  return (
    <Board data-testid="audit-logs-page">
      <Column>
        <Panel>
          <SecurityAuditToolbar
            getUserLogsLoading={loading}
            setDateRange={setDateRange}
            setFilters={setFilters}
            filters={filters}
            resetPagination={resetPagination}
          />
        </Panel>
      </Column>
      <Column>
        <Panel>
          <PanelHeader divider>
            <PanelHeading display="flex" flexDir="row">
              {title}
              {data?.getUserLogs?.total_items === 9999 && (
                <Box
                  data-testid={`security-audit-logs-limit-tooltip`}
                  textStyle="b1"
                  display="flex"
                  justifyItems="center"
                  margin="2px"
                >
                  <Tooltip
                    label={i18n.formatString(logsLimitMessage, '9999')}
                    placement="left"
                  >
                    <Box as="span" ml={1} cursor="pointer">
                      <InfoSharpIcon />
                    </Box>
                  </Tooltip>
                </Box>
              )}
            </PanelHeading>
          </PanelHeader>
          <FlushPanelContent>
            <SecurityAuditTable
              loading={loading}
              error={error}
              logs={logs}
              pageCount={pageCount}
              pageIndex={pageIndex}
              sortBy={sortBy}
              onFetchData={onFetchData}
              pageSize={pageSize}
              itemCount={data?.getUserLogs?.total_items || 0}
            />
          </FlushPanelContent>
        </Panel>
      </Column>
    </Board>
  );
};
